import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import { Briefcase, Download, CheckCircle, Clock, FileText, Star } from 'lucide-react';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const STATUS_BADGES = {
  new: 'bg-blue-100 text-blue-800', contacted: 'bg-yellow-100 text-yellow-800',
  engaged: 'bg-green-100 text-green-800', closed: 'bg-gray-100 text-gray-600'
};
const CONTRACT_BADGES = {
  draft: 'bg-gray-100 text-gray-700', signed: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800', completed: 'bg-purple-100 text-purple-800'
};

export default function MyEngagements() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingDraft, setRatingDraft] = useState({});
  const [submittingId, setSubmittingId] = useState(null);

  const submitFeedback = async (engagementId) => {
    const draft = ratingDraft[engagementId];
    if (!draft?.rating) { alert('Please select a star rating'); return; }
    setSubmittingId(engagementId);
    try {
      await axios.post(`${API_BASE_URL}/api/experts/engagements/${engagementId}/feedback`,
        { rating: draft.rating, comment: draft.comment || null },
        { headers: authHeaders(), withCredentials: true });
      await fetchEngagements();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmittingId(null);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchEngagements();
  }, [user, authLoading]);

  const fetchEngagements = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/experts/my-engagements`, {
        headers: authHeaders(), withCredentials: true
      });
      setEngagements(data.engagements);
    } catch (e) {
      console.error('Error fetching engagements:', e);
    } finally {
      setLoading(false);
    }
  };

  const downloadContract = async (en) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/experts/engagements/${en.id}/contract/download`,
        { headers: authHeaders(), withCredentials: true, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.download = en.contract?.signed_doc?.original_name || 'contract.pdf';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);
    } catch (e) { alert('Download failed'); }
  };

  const milestoneProgress = (ms) => {
    if (!ms || ms.length === 0) return null;
    const done = ms.filter(m => m.status === 'completed').length;
    return { done, total: ms.length, pct: Math.round((done / ms.length) * 100) };
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5F8D] pt-32 pb-10">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3" data-testid="my-engagements-title">
            <Briefcase className="h-8 w-8" />My Expert Engagements
          </h1>
          <p className="text-blue-100 mt-2">Track your assignments, contracts and progress — coordinated by CSED</p>
        </div>
      </div>

      <main className="flex-grow">
        <div className="container mx-auto px-4 py-10 max-w-4xl">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
            </div>
          ) : engagements.length === 0 ? (
            <Card className="p-12 text-center" data-testid="no-engagements-card">
              <Briefcase className="h-14 w-14 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No engagements yet</h3>
              <p className="text-gray-600 mb-6">Browse the Expert Panel and request an engagement to get started.</p>
              <Button onClick={() => navigate('/monitoring')} className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid="browse-experts-btn">
                Browse Expert Panel
              </Button>
            </Card>
          ) : (
            <div className="space-y-6">
              {engagements.map((en) => {
                const prog = milestoneProgress(en.contract?.milestones);
                return (
                  <Card key={en.id} className="p-6" data-testid={`my-engagement-card-${en.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                      <div>
                        <h3 className="font-bold text-lg text-[#1E3A5F]">{en.expert_name}</h3>
                        <p className="text-sm text-gray-500">{en.expert_specialization} • Requested {new Date(en.created_at).toLocaleDateString('en-IN')}</p>
                      </div>
                      <div className="flex gap-2">
                        <Badge className={STATUS_BADGES[en.status]}>{en.status}</Badge>
                        {en.contract && <Badge className={CONTRACT_BADGES[en.contract.status]}>Contract: {en.contract.status}</Badge>}
                      </div>
                    </div>

                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 mb-4">{en.project_brief}</p>

                    {en.contract && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm text-gray-600 flex-wrap gap-2">
                          <span><FileText className="h-4 w-4 inline mr-1 text-[#E87722]" />{en.contract.contract_number}
                            {en.contract.start_date && ` • ${en.contract.start_date} → ${en.contract.end_date || 'ongoing'}`}</span>
                          {en.contract.signed_doc && (
                            <button onClick={() => downloadContract(en)}
                              className="text-[#1E3A5F] underline flex items-center gap-1 text-sm"
                              data-testid={`download-contract-btn-${en.id}`}>
                              <Download className="h-3 w-3" />Signed Contract
                            </button>
                          )}
                        </div>

                        {prog && (
                          <div className="mt-3" data-testid={`milestone-progress-${en.id}`}>
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                              <span>Milestones: {prog.done}/{prog.total} completed</span>
                              <span>{prog.pct}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-[#E87722] h-2 rounded-full transition-all" style={{ width: `${prog.pct}%` }}></div>
                            </div>
                            <div className="mt-2 space-y-1">
                              {en.contract.milestones.map((m) => (
                                <div key={m.id} className="flex items-center gap-2 text-sm">
                                  {m.status === 'completed'
                                    ? <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                                    : <Clock className={`h-4 w-4 flex-shrink-0 ${m.status === 'in_progress' ? 'text-yellow-500' : 'text-gray-300'}`} />}
                                  <span className={m.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-700'}>{m.title}</span>
                                  {m.status === 'in_progress' && <Badge className="bg-yellow-100 text-yellow-800 text-[10px]">In progress</Badge>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {(en.progress_updates || []).length > 0 && (
                      <div data-testid={`progress-timeline-${en.id}`}>
                        <p className="text-sm font-semibold text-[#1E3A5F] mb-2">Latest Updates</p>
                        {en.progress_updates.slice(-3).reverse().map((u) => (
                          <div key={u.id} className="border-l-2 border-[#E87722]/40 pl-3 mb-2">
                            <p className="text-sm text-gray-700">{u.text}</p>
                            <p className="text-xs text-gray-400">{new Date(u.at).toLocaleString('en-IN')}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Feedback / Rating */}
                    {en.contract?.status === 'completed' && (
                      en.feedback ? (
                        <div className="mt-4 pt-4 border-t" data-testid={`feedback-given-${en.id}`}>
                          <p className="text-sm font-semibold text-[#1E3A5F] mb-1">Your Rating</p>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star key={s} className={`h-5 w-5 ${s <= en.feedback.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                            ))}
                          </div>
                          {en.feedback.comment && <p className="text-sm text-gray-600 mt-1 italic">"{en.feedback.comment}"</p>}
                        </div>
                      ) : (
                        <div className="mt-4 pt-4 border-t bg-amber-50/60 rounded-lg p-4" data-testid={`feedback-form-${en.id}`}>
                          <p className="text-sm font-semibold text-[#1E3A5F] mb-2">How was your experience with {en.expert_name}?</p>
                          <div className="flex items-center gap-1 mb-3">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <button key={s}
                                onClick={() => setRatingDraft(prev => ({ ...prev, [en.id]: { ...prev[en.id], rating: s } }))}
                                data-testid={`rating-star-${en.id}-${s}`}>
                                <Star className={`h-7 w-7 transition-colors ${s <= (ratingDraft[en.id]?.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 hover:text-yellow-300'}`} />
                              </button>
                            ))}
                          </div>
                          <textarea
                            className="w-full border rounded-md p-2 text-sm mb-3"
                            rows={2}
                            placeholder="Share a few words about the expert's work (optional)"
                            value={ratingDraft[en.id]?.comment || ''}
                            onChange={(e) => setRatingDraft(prev => ({ ...prev, [en.id]: { ...prev[en.id], comment: e.target.value } }))}
                            data-testid={`rating-comment-${en.id}`}
                          />
                          <Button size="sm" onClick={() => submitFeedback(en.id)} disabled={submittingId === en.id}
                            className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid={`submit-rating-btn-${en.id}`}>
                            {submittingId === en.id ? 'Submitting...' : 'Submit Rating'}
                          </Button>
                        </div>
                      )
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
